import { useState, useEffect } from 'react';
import { BookOpen, Check, X, Loader2, ExternalLink, User, Calendar, Star, AlertCircle } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

interface CuratedSourceRequest {
  id: string;
  title: string;
  url: string;
  literature_type: string;
  description: string;
  ai_relevance_score: number | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  requester_name: string;
  requester_email: string;
  study_plan_title?: string;
  review_note?: string;
}

export default function CuratedSourceRequests() {
  const [requests, setRequests] = useState<CuratedSourceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectModal, setShowRejectModal] = useState<string | null>(null);
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authenticatedFetch('/api/curated-sources/requests');
      const data = await response.json();

      if (data.success) {
        setRequests(data.requests || []);
      } else {
        setError(data.error || 'Kunne ikke laste forespørsler');
      }
    } catch (err) {
      setError('Kunne ikke laste forespørsler');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    setProcessingId(requestId);
    setError('');
    try {
      const response = await authenticatedFetch(`/api/curated-sources/requests/${requestId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Kilde godkjent og lagt til i kurert bibliotek');
        loadRequests();
      } else {
        setError(data.error || 'Kunne ikke godkjenne forespørsel');
      }
    } catch (err) {
      setError('Kunne ikke godkjenne forespørsel');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    setProcessingId(requestId);
    setError('');
    try {
      const response = await authenticatedFetch(`/api/curated-sources/requests/${requestId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_note: rejectNote })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Forespørsel avvist');
        setShowRejectModal(null);
        setRejectNote('');
        loadRequests();
      } else {
        setError(data.error || 'Kunne ikke avvise forespørsel');
      }
    } catch (err) {
      setError('Kunne ikke avvise forespørsel');
    } finally {
      setProcessingId(null);
    }
  };

  const filteredRequests = filter === 'pending'
    ? requests.filter(r => r.status === 'pending')
    : requests;

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  if (loading) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <Loader2 className="animate-spin mx-auto mb-2" size={32} />
          <p className="text-gray-600">Laster forespørsler...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="text-kf-primary" size={24} />
            <div>
              <h2 className="text-xl font-semibold">Litteraturforespørsler</h2>
              <p className="text-sm text-gray-600">
                Godkjenn eller avvis forespørsler om å legge kilder til kurert bibliotek
              </p>
            </div>
          </div>
          {pendingCount > 0 && (
            <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
              {pendingCount} venter
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check size={20} />
          {success}
        </div>
      )}

      {/* Filter */}
      <div className="card">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-kf-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Ventende ({pendingCount})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-kf-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Alle ({requests.length})
          </button>
        </div>
      </div>

      {/* Requests List */}
      <div className="card">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="mx-auto mb-3 text-gray-400" size={48} />
            <p className="text-gray-600 font-medium">
              {filter === 'pending' ? 'Ingen ventende forespørsler' : 'Ingen forespørsler'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Forespørsler fra medlemmer om å legge til kilder i kurert bibliotek vises her.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => (
              <div
                key={request.id}
                className={`border rounded-lg p-4 ${
                  request.status === 'approved'
                    ? 'border-green-200 bg-green-50'
                    : request.status === 'rejected'
                    ? 'border-red-200 bg-red-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Title and URL */}
                    <h3 className="font-semibold text-gray-900">{request.title}</h3>
                    <a
                      href={request.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-kf-primary hover:underline flex items-center gap-1 mt-1"
                    >
                      {request.url.length > 60 ? request.url.substring(0, 60) + '...' : request.url}
                      <ExternalLink size={12} />
                    </a>

                    {/* Description */}
                    {request.description && (
                      <p className="text-sm text-gray-600 mt-2">{request.description}</p>
                    )}

                    {/* Metadata */}
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <User size={12} />
                        {request.requester_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(request.created_at).toLocaleDateString('nb-NO')}
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                        {request.literature_type}
                      </span>
                      {request.ai_relevance_score && (
                        <span className="flex items-center gap-1">
                          <Star size={12} className="text-yellow-500" />
                          KI-relevans: {Math.round(request.ai_relevance_score * 100)}%
                        </span>
                      )}
                      {request.study_plan_title && (
                        <span className="text-gray-500">
                          Fra studieplan: {request.study_plan_title}
                        </span>
                      )}
                    </div>

                    {/* Review note for rejected */}
                    {request.status === 'rejected' && request.review_note && (
                      <div className="mt-2 text-sm text-red-700 bg-red-100 px-3 py-2 rounded">
                        Avvist: {request.review_note}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {request.status === 'pending' ? (
                      <>
                        <button
                          onClick={() => handleApprove(request.id)}
                          disabled={processingId === request.id}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
                        >
                          {processingId === request.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Check size={16} />
                          )}
                          Godkjenn
                        </button>
                        <button
                          onClick={() => setShowRejectModal(request.id)}
                          disabled={processingId === request.id}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
                        >
                          <X size={16} />
                          Avvis
                        </button>
                      </>
                    ) : (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        request.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {request.status === 'approved' ? 'Godkjent' : 'Avvist'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4">Avvis forespørsel</h3>
            <p className="text-sm text-gray-600 mb-4">
              Gi en begrunnelse for hvorfor forespørselen avvises (valgfritt).
            </p>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              className="w-full border rounded-lg p-3 text-sm"
              rows={3}
              placeholder="Begrunnelse..."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectNote('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Avbryt
              </button>
              <button
                onClick={() => handleReject(showRejectModal)}
                disabled={processingId === showRejectModal}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {processingId === showRejectModal && (
                  <Loader2 size={16} className="animate-spin" />
                )}
                Avvis forespørsel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
